import { API_URL } from "@/constants/api";

// Action type constants matching backend
export const ACTION_TYPES = {
  MERGE: "MERGE",
  RENAME: "RENAME",
  EDIT: "EDIT",
  CREATE: "CREATE",
  DECLINE: "DECLINE",
  NOTE: "NOTE",
  UNDO_MERGE: "UNDO_MERGE",
  UNDO_RENAME: "UNDO_RENAME",
  UNDO_EDIT: "UNDO_EDIT",
} as const;

export type ActionType = typeof ACTION_TYPES[keyof typeof ACTION_TYPES];

export interface SpotAdminLog {
  id: number;
  actionType: ActionType;
  adminUserId: number;
  adminUsername?: string;
  createdAt: string;
  spotId?: number;
  spotName: string;
  sourceSpotId?: number;
  sourceSpotName?: string;
  targetSpotId?: number;
  targetSpotName?: string;
  snapshot?: string;
  notes?: string;
  isUndone: boolean;
  undoneAt?: string;
  undoneBy?: number;
  undoneByUsername?: string;
  undoBlockedReason?: string;
  mergeRequestId?: number;
  relatedLogId?: number;
  canUndo?: boolean;
  undoWarnings?: string[];
  sessionsAffected?: number;
  reviewsAffected?: number;
}

export interface UndoResult {
  success: boolean;
  message?: string;
  warnings?: string[];
}

export class SpotAdminLogService {
  /**
   * Get admin log entries for a specific spot
   */
  static async getSpotLogs(spotId: number, accessToken: string): Promise<SpotAdminLog[]> {
    try {
      const response = await fetch(
        `${API_URL}/spotadminlog/spot/${spotId}/${accessToken}`
      );

      if (!response.ok) {
        console.error("Failed to fetch spot admin logs:", response.status);
        return [];
      }

      const data = await response.json();
      // Ensure we always return an array
      if (!Array.isArray(data)) {
        console.error("API returned non-array response:", data);
        return [];
      }
      return data;
    } catch (error) {
      console.error("Error fetching spot admin logs:", error);
      return [];
    }
  }

  /**
   * Get recent admin log entries across all spots
   */
  static async getRecentLogs(
    accessToken: string,
    limit: number = 50,
    offset: number = 0,
    actionType?: ActionType
  ): Promise<SpotAdminLog[]> {
    try {
      let url = `${API_URL}/spotadminlog/recent/${accessToken}?limit=${limit}&offset=${offset}`;
      if (actionType) {
        url += `&actionType=${actionType}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        console.error("Failed to fetch recent admin logs:", response.status);
        return [];
      }

      const data = await response.json();
      // Backend returns { total: number, items: SpotAdminLog[] }
      if (data && Array.isArray(data.items)) {
        return data.items;
      }
      // Fallback for direct array response
      if (Array.isArray(data)) {
        return data;
      }
      console.error("API returned unexpected response format:", data);
      return [];
    } catch (error) {
      console.error("Error fetching recent admin logs:", error);
      return [];
    }
  }

  /**
   * Add a note to a spot
   */
  static async addNote(
    spotId: number,
    spotName: string,
    note: string,
    accessToken: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(
        `${API_URL}/spotadminlog/note/${spotId}/${accessToken}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ note }),
        }
      );

      if (!response.ok) {
        return { success: false, message: "Failed to add note" };
      }

      return { success: true };
    } catch (error) {
      console.error("Error adding note:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Undo an admin action
   */
  static async undoAction(logId: number, accessToken: string, confirmWarnings: boolean = false): Promise<UndoResult> {
    try {
      const url = confirmWarnings
        ? `${API_URL}/spotadminlog/undo/${logId}/${accessToken}?confirmWarnings=true`
        : `${API_URL}/spotadminlog/undo/${logId}/${accessToken}`;

      const response = await fetch(url, { method: "POST" });

      const text = await response.text();

      if (!response.ok) {
        try {
          const errorData = JSON.parse(text);
          return {
            success: false,
            message: errorData.message || errorData.error || "Undo failed"
          };
        } catch {
          return { success: false, message: text || "Undo failed" };
        }
      }

      try {
        const data = JSON.parse(text);

        // Backend returns {success: false, requiresConfirmation: true} with HTTP 200
        // when warnings need user confirmation before proceeding
        if (data.requiresConfirmation) {
          return {
            success: false,
            message: "This action requires confirmation",
            warnings: data.warnings
          };
        }

        return {
          success: data.success !== false,
          message: data.message || data.error,
          warnings: data.warnings
        };
      } catch {
        return { success: true };
      }
    } catch (error) {
      console.error("Error undoing action:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Format action type for display
   */
  static formatActionType(actionType: ActionType): string {
    switch (actionType) {
      case ACTION_TYPES.MERGE:
        return "Merge";
      case ACTION_TYPES.RENAME:
        return "Rename";
      case ACTION_TYPES.EDIT:
        return "Edit";
      case ACTION_TYPES.CREATE:
        return "Create";
      case ACTION_TYPES.DECLINE:
        return "Decline";
      case ACTION_TYPES.NOTE:
        return "Note";
      case ACTION_TYPES.UNDO_MERGE:
        return "Undo Merge";
      case ACTION_TYPES.UNDO_RENAME:
        return "Undo Rename";
      case ACTION_TYPES.UNDO_EDIT:
        return "Undo Edit";
      default:
        return actionType;
    }
  }

  /**
   * Get color for action type badge
   */
  static getActionTypeColor(actionType: ActionType): { bg: string; text: string } {
    switch (actionType) {
      case ACTION_TYPES.MERGE:
        return { bg: "#7c3aed", text: "#ffffff" }; // Purple
      case ACTION_TYPES.RENAME:
        return { bg: "#2563eb", text: "#ffffff" }; // Blue
      case ACTION_TYPES.EDIT:
        return { bg: "#0891b2", text: "#ffffff" }; // Cyan
      case ACTION_TYPES.CREATE:
        return { bg: "#16a34a", text: "#ffffff" }; // Green
      case ACTION_TYPES.DECLINE:
        return { bg: "#dc2626", text: "#ffffff" }; // Red
      case ACTION_TYPES.NOTE:
        return { bg: "#6b7280", text: "#ffffff" }; // Gray
      case ACTION_TYPES.UNDO_MERGE:
      case ACTION_TYPES.UNDO_RENAME:
      case ACTION_TYPES.UNDO_EDIT:
        return { bg: "#f59e0b", text: "#000000" }; // Amber
      default:
        return { bg: "#374151", text: "#ffffff" }; // Default gray
    }
  }

  /**
   * Format relative time
   */
  static formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  }
}
